import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types';
import AuthService from '../services/authService';
import { getIdleTimeoutMs, getSessionMode } from '../utils/sessionPreferences';
import { getRecorderStatusSnapshot } from '../utils/recorderEvents';
import { logger } from '../utils/logger';

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isLoggedIn: boolean;
    sessionExpired: boolean;
    idleTimeRemaining: number | null; // Time remaining in milliseconds, null if not using idle_timeout
    login: (username: string, password: string) => Promise<boolean>;
    determineLoginMethod: (email: string) => Promise<{ method: 'code' | 'password' | 'both' | 'sso'; orgIdentifier?: string }>;
    verifyPassword: (email: string, password: string) => Promise<boolean>;
    requestLoginCode: (email: string) => Promise<boolean>;
    verifyLoginCode: (email: string, code: string) => Promise<boolean>;
    getSsoLoginUrl: (orgIdentifier: string) => string;
    exchangeSsoCode: (code: string) => Promise<boolean>;
    logout: () => Promise<void>;
    handleTokenExpiry: () => void;
    clearSessionExpired: () => void;
    setDomain: (domain: string) => Promise<void>;
    getDomain: () => string;
    checkDomainStatus: () => Promise<boolean>;
}

const ACTIVITY_STORAGE_KEY = 'notizy_last_activity';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [idleTimeRemaining, setIdleTimeRemaining] = useState<number | null>(null);
    const authService = AuthService.getInstance();

    const initializeAuth = useCallback(async () => {
        try {
            if (authService.isLoggedIn()) {
                // Try to fetch fresh user data from server first
                const bootstrappedUser = await authService.fetchBootstrap();
                if (bootstrappedUser) {
                    setUser(bootstrappedUser);
                } else {
                    // Fallback to cached user data
                    const currentUser = await authService.getCurrentUser();
                    setUser(currentUser);
                }
            }
        } catch (error) {
            logger.error('Error initializing auth:', error);
        } finally {
            setIsLoading(false);
        }
    }, [authService]);

    useEffect(() => {
        // Registreer callback voor token expiry
        authService.setTokenExpiryCallback(() => {
            setSessionExpired(true);
        });

        // Registreer callback voor user changes (bijv. na token refresh)
        authService.setUserChangeCallback(async () => {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
        });

        initializeAuth();
    }, [authService, initializeAuth]);

    useEffect(() => {
        if (!user) {
            setIdleTimeRemaining(null);
            return;
        }

        if (getSessionMode() !== 'idle_timeout') {
            setIdleTimeRemaining(null);
            return;
        }

        // const ACTIVITY_STORAGE_KEY = 'notizy_last_activity'; // Moved to top level
        let didExpire = false;
        const idleTimeoutMs = getIdleTimeoutMs();
        const checkIntervalMs = 1000; // Check every second for accurate countdown
        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'focus'];
        const passiveOptions: AddEventListenerOptions = { passive: true };

        // Get last activity from localStorage (shared across tabs)
        const getLastActivity = (): number => {
            const stored = localStorage.getItem(ACTIVITY_STORAGE_KEY);
            if (stored) {
                const parsed = parseInt(stored, 10);
                if (!isNaN(parsed)) {
                    return parsed;
                }
            }
            return Date.now();
        };

        // Save activity to localStorage (visible to all tabs)
        const markActivity = () => {
            const now = Date.now();
            localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                markActivity();
            }
        };

        // Listen for activity from other tabs
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === ACTIVITY_STORAGE_KEY && event.newValue) {
                // Another tab updated activity - our next checkIdle will see the new value
            }
        };

        const checkIdle = () => {
            if (didExpire) {
                return;
            }

            const recorderStatus = getRecorderStatusSnapshot();
            if (recorderStatus?.isRecording || recorderStatus?.isPaused || recorderStatus?.isUploading) {
                markActivity();
                setIdleTimeRemaining(idleTimeoutMs);
                return;
            }

            const lastActivity = getLastActivity();
            const elapsed = Date.now() - lastActivity;
            const remaining = idleTimeoutMs - elapsed;
            setIdleTimeRemaining(Math.max(0, remaining));

            if (remaining <= 0) {
                didExpire = true;
                authService.handleTokenExpiry();
            }
        };

        // Initialize with current time if no activity recorded
        if (!localStorage.getItem(ACTIVITY_STORAGE_KEY)) {
            markActivity();
        }

        activityEvents.forEach((eventName) => window.addEventListener(eventName, markActivity, passiveOptions));
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('storage', handleStorageChange);

        const intervalId = window.setInterval(checkIdle, checkIntervalMs);
        checkIdle(); // Initial check

        return () => {
            activityEvents.forEach((eventName) => window.removeEventListener(eventName, markActivity));
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('storage', handleStorageChange);
            window.clearInterval(intervalId);
        };
    }, [user, authService]);

    const login = async (username: string, password: string): Promise<boolean> => {
        try {
            const success = await authService.login(username, password);
            if (success) {
                const currentUser = await authService.getCurrentUser();
                setUser(currentUser);
                // Reset activity timer to prevent immediate idle timeout from stale data
                localStorage.setItem(ACTIVITY_STORAGE_KEY, Date.now().toString());
            }
            return success;
        } catch (error) {
            logger.error('Login error:', error);
            return false;
        }
    };

    const determineLoginMethod = async (email: string): Promise<{ method: 'code' | 'password' | 'both' | 'sso'; orgIdentifier?: string }> => {
        return await authService.determineLoginMethod(email);
    };

    const verifyPassword = async (email: string, password: string): Promise<boolean> => {
        const success = await authService.verifyPassword(email, password);
        if (success) {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
            localStorage.setItem(ACTIVITY_STORAGE_KEY, Date.now().toString());
        }
        return success;
    };

    const requestLoginCode = async (email: string): Promise<boolean> => {
        return await authService.requestLoginCode(email);
    };

    const verifyLoginCode = async (email: string, code: string): Promise<boolean> => {
        try {
            const success = await authService.verifyLoginCode(email, code);
            if (success) {
                const currentUser = await authService.getCurrentUser();
                setUser(currentUser);
                // Reset activity timer on successful login
                localStorage.setItem(ACTIVITY_STORAGE_KEY, Date.now().toString());
            }
            return success;
        } catch (error) {
            logger.error('Code verification error:', error);
            return false;
        }
    };

    const getSsoLoginUrl = (orgIdentifier: string): string => {
        return authService.getSsoLoginUrl(orgIdentifier);
    };

    const exchangeSsoCode = async (code: string): Promise<boolean> => {
        try {
            const success = await authService.exchangeSsoCode(code);
            if (success) {
                const currentUser = await authService.getCurrentUser();
                setUser(currentUser);
                // Reset activity timer on successful SSO login
                localStorage.setItem(ACTIVITY_STORAGE_KEY, Date.now().toString());
            }
            return success;
        } catch (error) {
            logger.error('SSO code exchange error:', error);
            return false;
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
            setUser(null);
        } catch (error) {
            logger.error('Logout error:', error);
        }
    };

    const handleTokenExpiry = () => {
        authService.handleTokenExpiry();
    };

    const clearSessionExpired = () => {
        setSessionExpired(false);
    };

    const setDomain = async (domain: string): Promise<void> => {
        await authService.setDomain(domain);
    };

    const getDomain = (): string => {
        return authService.getDomain();
    };

    const checkDomainStatus = async (): Promise<boolean> => {
        return await authService.checkDomainStatus();
    };

    const value: AuthContextType = {
        user,
        isLoading,
        isLoggedIn: !!user,
        sessionExpired,
        idleTimeRemaining,
        login,
        determineLoginMethod,
        verifyPassword,
        requestLoginCode,
        verifyLoginCode,
        getSsoLoginUrl,
        exchangeSsoCode,
        logout,
        handleTokenExpiry,
        clearSessionExpired,
        setDomain,
        getDomain,
        checkDomainStatus,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}; 
